import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToggler } from '@cbhq/cds-common';
import { Switch } from '@cbhq/cds-web/controls/Switch';
import { Icon } from '@cbhq/cds-web/icons';
import { Pictogram, SpotIcon } from '@cbhq/cds-web/illustrations';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Pressable } from '@cbhq/cds-web/system';
import { TextBody } from '@cbhq/cds-web/typography/TextBody';
import { TextHeadline } from '@cbhq/cds-web/typography/TextHeadline';
import { TextTitle3 } from '@cbhq/cds-web/typography/TextTitle3';

import { useIsBaseAAEnabled } from ':cloud/hooks/featureFlags/useIsBaseAAEnabled';
import { useFindOrCreateToken } from ':cloud/hooks/useFindOrCreateToken';
import { useGetProject } from ':cloud/hooks/useGetProject';
import { useProjectProducts } from ':cloud/hooks/useProjectProducts';
import { EventName, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { useCreateGasPolicy } from ':cloud/queries/Base/useCreateGasPolicy';
import {
  useUpdateAccountAbstractionStatus,
  useUpdateGasPolicy,
} from ':cloud/queries/Base/useUpdateGasPolicy';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useUpdateProject } from ':cloud/queries/Projects/useUpdateProject';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { GasPolicy, Project } from ':cloud/types/ts_types';
import { generateProjectNameSchema } from ':cloud/utils/validate';
import DisableAccountAbstractionWarningModal from ':cloud/widgets/base/DisableAccountAbstractionWarningModal';
import { GasPolicyForm } from ':cloud/widgets/base/GasPolicyForm';
import { getProductName } from ':cloud/widgets/helpers';
import { Layout } from ':cloud/widgets/layout';
import { CloudErrorBoundary } from ':cloud/widgets/sharedcomponents';
import { EditableValue } from ':cloud/widgets/sharedcomponents/EditableValue';
import { ErrorView } from ':cloud/widgets/sharedcomponents/ErrorView';

function BasicInfoSection() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = useGetProject(projectId);
  const {
    user: { isOwnerOrAdmin },
    activeOrg,
  } = useGetUser();
  const { projects } = useGetProjects(activeOrg?.organizationId);

  const updateProjectMutation = useUpdateProject();

  const onSubmit = useCallback(
    (name, onSettled) => {
      updateProjectMutation.mutate(
        { id: projectId, name, organizationId: activeOrg?.organizationId ?? '' },
        {
          onSettled,
        },
      );
    },
    [activeOrg?.organizationId, projectId, updateProjectMutation],
  );

  const validationSchema = useMemo(() => {
    const existingProjectNames = projects.map((p) => p.name);
    return generateProjectNameSchema({ value: project?.name }, existingProjectNames);
  }, [project, projects]);

  return (
    <VStack bordered borderRadius="rounded" overflow="hidden">
      <HStack spacing={5}>
        <TextTitle3 as="h3">Basic info</TextTitle3>
      </HStack>
      <VStack borderedTop justifyContent="center" alignItems="center">
        <EditableValue
          name="Name"
          value={project?.name}
          onSubmit={onSubmit}
          disabled={!isOwnerOrAdmin}
          validationSchema={validationSchema}
        />
      </VStack>
    </VStack>
  );
}

function ConnectedProductsSection() {
  const { projectId } = useParams<{ projectId: string }>();
  const connectedProducts = useProjectProducts(projectId);

  const getProductIcon = useCallback((productName: string) => {
    if (productName === 'Base RPC Endpoint') {
      return <Pictogram name="baseLogoNavigation" dimension="48x48" scaleMultiplier={0.5} />;
    }
    if (productName === 'Staking') {
      return <SpotIcon name="stakingProduct" dimension="24x24" />;
    }
    // TODO: [WAAS-2940] Update billing product name to `Embedded Wallets`.
    if (productName === 'Wallet as a Service' || productName === 'WaaS Consumer') {
      return <Pictogram name="walletAsServiceNavigation" dimension="48x48" scaleMultiplier={0.5} />;
    }
    return null;
  }, []);

  if (!connectedProducts.length) return null;

  //   const handlePress = useCallback(() => {}, []);
  return (
    <VStack bordered borderRadius="rounded">
      <HStack justifyContent="space-between" alignItems="center" spacing={5}>
        <VStack gap={1}>
          <TextTitle3 as="h3">Connected products</TextTitle3>
          {/* TODO: add back when management is enabled */}
          {/* <TextBody as="p" color="foregroundMuted">
            Manage the products that are connected to this project.
          </TextBody> */}
        </VStack>
        <VStack>
          {/* TODO: re-enable when we can add multiple products to projects */}
          {/* <Button compact variant="primary" onPress={handlePress}>
            Add a product
          </Button> */}
        </VStack>
        <VStack />
      </HStack>
      <VStack>
        {connectedProducts.map((product) => {
          return (
            <HStack gap={2} borderedTop spacing={5} key={product.productName} alignItems="center">
              {getProductIcon(product.productName)}
              {/* TODO: temp solution, we need to drop dependence on the backend for productName */}
              <TextHeadline as="p">{getProductName(product.productName)}</TextHeadline>
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
}

function DeleteProjectSection() {
  return (
    <VStack bordered borderRadius="rounded" spacing={5}>
      <HStack justifyContent="space-between" alignItems="center">
        <VStack gap={1}>
          <TextTitle3 as="h3">Delete</TextTitle3>
          <TextBody as="p" color="foregroundMuted">
            Project deletion is currently not supported
          </TextBody>
        </VStack>
        <VStack>
          {/* <Button variant="negative" onPress={handlePress} disabled>
            Delete
          </Button> */}
        </VStack>
      </HStack>
    </VStack>
  );
}

type PaymasterAndBundlerSectionProps = {
  project: Project;
  organizationId: string;
};

function PaymasterAndBundlerSection({ project, organizationId }: PaymasterAndBundlerSectionProps) {
  const [isGasPolicyFormOpen, setIsGasPolicyFormOpen] = useState(false);
  /* When the user initially comes to the ProjectManage page we want 
  the form to be open if Paymaster & Bundler is enabled. This state 
  keeps track of whether or not the default state has been applied 
  so that it doesn't continue to open the form when changes are made.  */
  const [hasSetInitialFormState, setHasSetInitialFormState] = useState(false);
  const [warningModalVisible, { toggleOn: showWarningModal, toggleOff: hideWarningModal }] =
    useToggler();

  const { projectId } = useParams<{ projectId: string }>();
  const { token } = useFindOrCreateToken(
    organizationId,
    projectId,
    project.type,
    project.isTestnet,
  );

  const toast = useToast();

  const gasPolicy = token?.policies?.[0];

  useEffect(() => {
    if (!hasSetInitialFormState && !isGasPolicyFormOpen && gasPolicy?.enabled) {
      setIsGasPolicyFormOpen(true);
      setHasSetInitialFormState(true);
    }
  }, [gasPolicy?.enabled, hasSetInitialFormState, isGasPolicyFormOpen]);

  const createGasPolicy = useCreateGasPolicy({
    organizationId,
    projectId,
    tokenId: token?.id,
    onError: (errorMessage) => {
      toast.show(errorMessage, { variant: 'negative' });
    },
  });

  const updateGasPolicy = useUpdateGasPolicy({
    organizationId,
    projectId,
  });

  const updateAccountAbstractionSuccessMsg = useMemo(() => {
    if (gasPolicy?.enabled) {
      return 'Paymaster & Bundler disabled';
    }
    return 'Paymaster & Bundler enabled';
  }, [gasPolicy]);

  const updateAccountAbstractionStatus = useUpdateAccountAbstractionStatus({
    organizationId,
    projectId,
    successMsg: updateAccountAbstractionSuccessMsg,
  });

  const handleAccountAbstractionStatusChange = useCallback(() => {
    if (!gasPolicy) {
      createGasPolicy.mutate();
      return;
    }

    const policy = { ...gasPolicy, enabled: !gasPolicy?.enabled };

    if (!policy.enabled) {
      showWarningModal();
      return;
    }
    updateAccountAbstractionStatus.mutate({ policy });
  }, [createGasPolicy, gasPolicy, showWarningModal, updateAccountAbstractionStatus]);

  const handleDisableAccountAbstraction = useCallback(() => {
    if (!gasPolicy) {
      throw new Error('Unable to disable Paymaster & Bundler. Missing required information.');
    }

    const policy = { ...gasPolicy, enabled: !gasPolicy?.enabled };

    if (!policy.enabled) {
      setIsGasPolicyFormOpen(false);
    }
    updateAccountAbstractionStatus.mutate({ policy });
  }, [gasPolicy, updateAccountAbstractionStatus]);

  const handleToggleGasPolicyFormPress = useCallback(() => {
    setIsGasPolicyFormOpen((prev) => !prev);
  }, []);

  const isAccountAbstractionEnabled = !!gasPolicy?.enabled;

  const handlePublishGasPolicy = useCallback(
    (policy: GasPolicy) => {
      updateGasPolicy.mutate({ policy });
      setIsGasPolicyFormOpen(false);
    },
    [updateGasPolicy],
  );

  return (
    <CloudErrorBoundary name="ManagePaymasterAndBundler" fallback={<ErrorView />} isComponentLevel>
      <VStack bordered borderRadius="rounded">
        {warningModalVisible && gasPolicy && (
          <DisableAccountAbstractionWarningModal
            onRequestClose={hideWarningModal}
            onConfirm={handleDisableAccountAbstraction}
          />
        )}
        <HStack justifyContent="space-between" alignItems="center" spacing={5}>
          <VStack gap={1}>
            <TextTitle3 as="h3">Paymaster & Bundler</TextTitle3>
          </VStack>
          <Switch
            disabled={createGasPolicy.isLoading || updateAccountAbstractionStatus.isLoading}
            onChange={handleAccountAbstractionStatusChange}
            checked={isAccountAbstractionEnabled}
          >
            {isAccountAbstractionEnabled ? 'Enabled' : 'Disabled'}
          </Switch>
        </HStack>
        <VStack spacing={5} borderedTop>
          <Pressable
            background="transparent"
            onPress={handleToggleGasPolicyFormPress}
            disabled={!isAccountAbstractionEnabled}
            noScaleOnPress
          >
            <HStack justifyContent="space-between">
              <TextHeadline as="p">Gas policy</TextHeadline>

              <HStack alignItems="center" height={22}>
                {!isGasPolicyFormOpen && (
                  <TextBody as="p" spacingEnd={3}>
                    Edit
                  </TextBody>
                )}
                <Icon name={isGasPolicyFormOpen ? 'caretUp' : 'caretRight'} size="s" />
              </HStack>
            </HStack>
          </Pressable>
          {isGasPolicyFormOpen && gasPolicy && (
            <GasPolicyForm
              onCancel={handleToggleGasPolicyFormPress}
              policy={gasPolicy}
              onPublishGasPolicy={handlePublishGasPolicy}
            />
          )}
        </VStack>
      </VStack>
    </CloudErrorBoundary>
  );
}

function ProjectManageContent() {
  const isBaseAAEnabled = useIsBaseAAEnabled();
  useLogViewOnMount(EventName.v2_manage_page_view);
  const { projectId } = useParams<{ projectId: string }>();
  const project = useGetProject(projectId);
  const { activeOrg } = useGetUser();

  return (
    <VStack alignItems="center" spacingTop={2}>
      <VStack gap={5} maxWidth={760} width="100%">
        <BasicInfoSection />
        <ConnectedProductsSection />
        {isBaseAAEnabled && project?.type === 'base' && activeOrg && (
          <PaymasterAndBundlerSection project={project} organizationId={activeOrg.organizationId} />
        )}
        <DeleteProjectSection />
      </VStack>
    </VStack>
  );
}

export function ProjectManage() {
  return (
    <Layout>
      <Layout.MainContainer fullWidth>
        <CloudErrorBoundary name="ProjectManage">
          <ProjectManageContent />
        </CloudErrorBoundary>
      </Layout.MainContainer>
    </Layout>
  );
}
